import { useMemo, useState } from 'react';
import {
  ArrowRight,
  BookOpen,
  ChevronDown,
  ChevronUp,
  CreditCard,
  Heart,
  HeartHandshake,
  Loader2,
  ShieldCheck,
  Stethoscope,
  Users,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useDonorRollup } from '@/lib/use-donor-rollup';

interface CustomerDashboardPageProps {
  readonly displayName: string;
  readonly userId: string;
}

interface TimestampLike {
  toDate: () => Date;
}

interface DonorRollupRecord {
  totalDonated: number;
  donationCount: number;
  monthlyDonationCount: number;
  oneTimeDonationCount: number;
  lastDonationAt: TimestampLike | null;
  lastDonationAmount: number;
  lastDonationFrequency: 'monthly' | 'one_time' | null;
  currency: string;
}

const quickActionsConfig = [
  {
    id: 'donate' as const,
    title: 'Give support',
    description: 'Make a one-time or monthly gift for CKD warriors and families in the Philippines.',
    icon: Heart,
    cta: 'Donate now',
    path: '/donate',
  },
  {
    id: 'donor' as const,
    title: 'Donor summary',
    description: 'View lifetime impact, donation mix, and your full supporter dashboard.',
    icon: HeartHandshake,
    cta: 'View donor summary',
    path: '/dashboard/donor',
  },
  {
    id: 'profile' as const,
    title: 'Community profile',
    description: 'Share how you want to stay involved so we can personalize CKD support, updates, and events.',
    icon: Users,
    cta: 'Update profile',
    path: '/dashboard/profile',
  },
  {
    id: 'billing' as const,
    title: 'Giving & receipts',
    description: 'Review your GSGO gift history, receipt status, and recurring monthly support.',
    icon: CreditCard,
    cta: 'Open giving history',
    path: '/dashboard/billing',
  },
];

const impactStepsConfig = [
  {
    number: 1,
    title: 'You give',
    description: 'Supporters worldwide give through secure checkout—one-time or monthly.',
    icon: Heart,
  },
  {
    number: 2,
    title: 'Needs are verified',
    description: 'GSGO coordinates with local partners to prioritize verified patient and family needs.',
    icon: ShieldCheck,
  },
  {
    number: 3,
    title: 'Care is delivered',
    description: 'Gifts support dialysis, medication, transplant pathways, and family-centered programs.',
    icon: Stethoscope,
  },
  {
    number: 4,
    title: 'Impact is shared',
    description: 'Stories and updates show how your generosity reaches CKD warriors and caregivers.',
    icon: BookOpen,
  },
];

function formatMoney(cents: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: (currency || 'usd').toUpperCase(),
    minimumFractionDigits: 2,
  }).format((cents || 0) / 100);
}

function formatDate(ts: TimestampLike | null): string {
  if (!ts) return '—';
  try {
    return ts.toDate().toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return '—';
  }
}

export function CustomerDashboardPage({
  displayName,
  userId,
}: CustomerDashboardPageProps): JSX.Element {
  const { donorRecord, loading: isDonorLoading } = useDonorRollup(userId, 'CustomerDashboardPage');
  const [isImpactExpanded, setIsImpactExpanded] = useState(false);

  const impactMessage = useMemo(() => {
    if (!donorRecord) {
      return 'Your dashboard is the home for giving, receipts, and updates as you stand with CKD warriors and families.';
    }
    if (donorRecord.monthlyDonationCount > 0) {
      return 'Thank you for your recurring support—reliable monthly gifts help families plan care with confidence.';
    }
    return 'Thank you for standing with CKD warriors. Every gift helps bridge global compassion to verified local care.';
  }, [donorRecord]);

  return (
    <main id="main-content" className="bg-gray-50" role="main" aria-label="Supporter dashboard">
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <div
          className="rounded-2xl overflow-hidden shadow-md border border-smb-primary-mid/30 p-6 sm:p-8"
          style={{
            background:
              'linear-gradient(135deg, var(--smb-palette-navy) 0%, var(--smb-palette-teal) 55%, var(--smb-palette-medium-blue) 100%)',
          }}
        >
          <p className="text-sm font-semibold tracking-wide uppercase text-smb-primary-light">
            GSGO Supporter Hub
          </p>
          <h1 className="mt-2 text-3xl sm:text-4xl font-bold text-white">Welcome back, {displayName}</h1>
          <p className="mt-1 text-sm font-medium text-white/90">Saving Lives. Bridging Worlds.</p>
          <p className="mt-3 text-sm sm:text-base text-white/85 max-w-2xl">{impactMessage}</p>
        </div>

        {isDonorLoading ? (
          <DonorStatsSkeleton />
        ) : donorRecord ? (
          <DonorImpactSummary donorRecord={donorRecord} />
        ) : (
          <FirstGiftCta />
        )}

        <QuickActionsGrid />

        <ExploreLinks />

        <ImpactJourneySection
          isExpanded={isImpactExpanded}
          onToggle={() => setIsImpactExpanded((prev) => !prev)}
        />
      </section>
    </main>
  );
}

function DonorStatsSkeleton(): JSX.Element {
  return (
    <div
      className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3"
      aria-busy="true"
      aria-label="Loading impact summary"
    >
      {['Lifetime giving', 'Total gifts', 'Last gift', 'Last gifted on'].map((label) => (
        <article key={label} className="rounded-xl border border-smb-slate-100 bg-white p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-smb-slate-600">{label}</p>
          <div className="mt-3 flex items-center gap-2 text-smb-slate-500">
            <Loader2 size={18} className="animate-spin" aria-hidden="true" />
            <span className="text-sm">Loading…</span>
          </div>
        </article>
      ))}
    </div>
  );
}

function DonorImpactSummary({ donorRecord }: { donorRecord: DonorRollupRecord }): JSX.Element {
  const stats = [
    {
      label: 'Lifetime giving',
      value: formatMoney(donorRecord.totalDonated, donorRecord.currency),
    },
    { label: 'Total gifts', value: String(donorRecord.donationCount) },
    {
      label: 'Last gift',
      value: formatMoney(donorRecord.lastDonationAmount, donorRecord.currency),
    },
    { label: 'Last gifted on', value: formatDate(donorRecord.lastDonationAt) },
  ];

  return (
    <section className="mt-8" aria-labelledby="impact-summary-heading">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
        <SectionHeading
          id="impact-summary-heading"
          eyebrow="Your impact"
          title="Thank you for standing with CKD warriors"
        />
        <Link
          to="/dashboard/donor"
          className="inline-flex items-center gap-2 text-sm font-semibold text-smb-primary hover:text-smb-primary-mid"
        >
          Full donor summary
          <ArrowRight size={16} aria-hidden="true" />
        </Link>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {stats.map(({ label, value }) => (
          <article key={label} className="rounded-xl border border-smb-slate-100 bg-white p-4 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wide text-smb-slate-600">{label}</p>
            <p className="mt-2 text-xl sm:text-2xl font-semibold text-smb-ink">{value}</p>
          </article>
        ))}
      </div>
      <p className="mt-3 text-xs text-smb-slate-600">
        {donorRecord.monthlyDonationCount > 0
          ? `Monthly gifts: ${donorRecord.monthlyDonationCount} · One-time gifts: ${donorRecord.oneTimeDonationCount}`
          : `One-time gifts: ${donorRecord.oneTimeDonationCount}`}
        {donorRecord.lastDonationFrequency === 'monthly' ? ' · You are a monthly supporter' : null}
      </p>
    </section>
  );
}

function FirstGiftCta(): JSX.Element {
  return (
    <section
      className="mt-8 rounded-2xl border border-smb-primary-mid/20 bg-white p-6 sm:p-8 shadow-sm"
      aria-labelledby="first-gift-heading"
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="max-w-xl">
          <HeartHandshake size={24} className="text-smb-primary-mid mb-3" aria-hidden="true" />
          <h2 id="first-gift-heading" className="text-xl font-bold text-smb-ink">
            Ready to make your first gift?
          </h2>
          <p className="mt-2 text-sm text-smb-slate-600">
            GSGO connects global donors to verified CKD patient needs in the Philippines—with transparency from
            gift to care.
          </p>
        </div>
        <Link
          to="/donate"
          className="inline-flex shrink-0 items-center justify-center gap-2 rounded-lg bg-smb-primary-mid px-5 py-2.5 text-sm font-semibold text-white hover:bg-smb-primary transition-colors"
        >
          Give now
          <ArrowRight size={16} aria-hidden="true" />
        </Link>
      </div>
    </section>
  );
}

function QuickActionsGrid(): JSX.Element {
  return (
    <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
      {quickActionsConfig.map(({ id, title, description, icon: Icon, cta, path }) => (
        <article
          key={id}
          className="rounded-2xl border border-smb-slate-100 bg-white p-5 shadow-sm hover:shadow-md transition-shadow"
        >
          <div className="w-11 h-11 rounded-xl bg-smb-primary-light/20 text-smb-primary flex items-center justify-center">
            <Icon size={20} aria-hidden="true" />
          </div>
          <h2 className="mt-4 text-lg font-semibold text-smb-ink">{title}</h2>
          <p className="mt-2 text-sm text-smb-slate-600">{description}</p>
          <Link
            to={path}
            className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-smb-primary hover:text-smb-primary-mid transition-colors"
          >
            {cta}
            <ArrowRight size={16} aria-hidden="true" />
          </Link>
        </article>
      ))}
    </div>
  );
}

function ExploreLinks(): JSX.Element {
  return (
    <section
      className="mt-8 rounded-2xl border border-smb-slate-100 bg-smb-surface/60 p-5 sm:p-6"
      aria-label="Explore GSGO"
    >
      <p className="text-sm font-semibold text-smb-ink">Explore GSGO</p>
      <div className="mt-3 flex flex-wrap gap-2">
        <Link
          to="/warriors"
          className="rounded-lg border border-smb-slate-100 bg-white px-4 py-2 text-sm font-semibold text-smb-ink hover:border-smb-primary-mid/40 transition-colors"
        >
          Meet CKD warriors
        </Link>
        <Link
          to="/articles"
          className="rounded-lg border border-smb-slate-100 bg-white px-4 py-2 text-sm font-semibold text-smb-ink hover:border-smb-primary-mid/40 transition-colors"
        >
          Read stories & updates
        </Link>
        <Link
          to="/about-us"
          className="rounded-lg border border-smb-slate-100 bg-white px-4 py-2 text-sm font-semibold text-smb-ink hover:border-smb-primary-mid/40 transition-colors"
        >
          Our mission
        </Link>
      </div>
    </section>
  );
}

function ImpactJourneySection({
  isExpanded,
  onToggle,
}: {
  isExpanded: boolean;
  onToggle: () => void;
}): JSX.Element {
  return (
    <div className="mt-8 rounded-2xl border border-smb-slate-100 bg-white shadow-sm overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-3 p-5 sm:p-6 text-left hover:bg-smb-slate-50/50 transition-colors focus:outline-none focus:ring-2 focus:ring-smb-primary-mid focus:ring-inset rounded-2xl"
        aria-expanded={isExpanded}
        aria-controls="impact-journey-content"
        id="impact-journey-heading"
      >
        <div>
          <p className="text-sm font-semibold tracking-wide uppercase text-smb-primary-mid">How GSGO works</p>
          <p className="mt-1 text-base sm:text-lg font-bold text-smb-ink">From your gift to care on the ground</p>
        </div>
        <span className="shrink-0 rounded-lg p-2 text-smb-slate-600 bg-smb-slate-100">
          {isExpanded ? <ChevronUp size={20} aria-hidden="true" /> : <ChevronDown size={20} aria-hidden="true" />}
        </span>
      </button>
      <section
        id="impact-journey-content"
        aria-labelledby="impact-journey-heading"
        className={isExpanded ? 'border-t border-smb-slate-100' : 'sr-only'}
      >
        {isExpanded && <ImpactJourneySteps />}
      </section>
    </div>
  );
}

function ImpactJourneySteps(): JSX.Element {
  return (
    <div className="p-5 sm:p-6 pt-4 sm:pt-5">
      <ImpactJourneyStepsGrid />
    </div>
  );
}

function ImpactJourneyStepsGrid(): JSX.Element {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {impactStepsConfig.map(({ number, title, description, icon: Icon }) => (
        <article
          key={number}
          className="rounded-xl border border-smb-slate-100 bg-smb-slate-50/40 p-4"
        >
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-smb-primary-light/20 text-smb-primary flex items-center justify-center shrink-0">
              <Icon size={20} aria-hidden="true" />
            </div>
            <div className="min-w-0">
              <h3 className="text-base font-semibold text-smb-ink">
                {number}. {title}
              </h3>
              <p className="mt-1 text-sm text-smb-slate-600">{description}</p>
            </div>
          </div>
        </article>
      ))}
    </div>
  );
}

function SectionHeading({
  id,
  eyebrow,
  title,
}: {
  id?: string;
  eyebrow: string;
  title: string;
}): JSX.Element {
  return (
    <div>
      <p className="text-sm font-semibold tracking-wide uppercase text-smb-primary-mid">{eyebrow}</p>
      <h2 id={id} className="mt-1 text-xl sm:text-2xl font-bold text-smb-ink">
        {title}
      </h2>
    </div>
  );
}
